import React, { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  useDisclosure,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from "@nextui-org/react";

import DefaultLayout from "@/layouts/default";
import { booksAPI, ordersAPI } from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "@/components/icons";

interface OrderListItem {
  id: number;
  book_id: number | string;
  book_name: string;
  status: string;
  quantity: number;
  supplier_name?: string;
  phone_number?: string;
  price_per_book?: number;
  total_purchase?: number;
  client_id: number | string;
}

interface Book {
  id: string;
  name: string;
  author?: string;
  category?: string;
  stock?: number;
  price?: number;
  min_stocks?: number;
  price_per_book?: number;
}

const OrderPage: React.FC = () => {
  const [addedBooks, setAddedBooks] = useState<OrderListItem[]>([]);
  const [pendingBooks, setPendingBooks] = useState<OrderListItem[]>([]);
  const [selectedBooks, setSelectedBooks] = useState<Set<number>>(new Set());
  const [selectedBookspending, setSelectedBookspending] = useState<Set<number>>(new Set());
  const [modalData, setModalData] = useState<{
    id: number;
    book_name: string;
  } | null>(null);
  const [modalDataBulk, setModalDataBulk] = useState<
    | {
        id: number;
        book_name: string;
        quantity: string;
      }[]
    | null
  >(null);
  const [formValues, setFormValues] = useState({
    name: "",
    phoneNumber: "",
    quantity: "",
  });
  const [completeModalData, setCompleteModalData] = useState<{
    id: number;
    book_name: string;
  } | null>(null);
  const [isCompleteModalOpen, setCompleteModalOpen] = useState(false);
  const [completeFormValues, setCompleteFormValues] = useState({
    originalQuantity: "",
    quantity: "",
    pricePerBook: "",
    totalPurchase: "",
    price: "",
  });
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const [bulkCompleteModalOpen, setBulkCompleteModalOpen] = useState(false);
  const [bulkCompleteData, setBulkCompleteData] = useState<Array<{
    id: number;
    book_name: string;
    quantity: string;
    pricePerBook: string;
    totalPurchase: string;
    originalQuantity: string;
    price: string;
  }> | null>(null);
  
  // New state for new order modal
  const [isNewOrderModalOpen, setNewOrderModalOpen] = useState(false);
  const [availableBooks, setAvailableBooks] = useState<Book[]>([]);
  const [selectedBookId, setSelectedBookId] = useState<string>("");
  const [newOrderFormValues, setNewOrderFormValues] = useState({
    book_id: "",
    book_name: "",
    quantity: "1",
    supplier_name: "",
    phone_number: "",
    status: "pending",
  });
  
  // Import useAuth for future use but currently not using the user object
  const { user: _user } = useAuth();
  const { t } = useTranslation();
  
  // Define a noop function for _setLowStockBooks
  const setLowStockBooks = (books: Book[]) => {
    // This is a noop function that does nothing but prevents the "unused" error
    console.log(`Would set ${books.length} low stock books if implemented`);
  };

  useEffect(() => {
    const fetchOrderList = async () => {
      try {
        const orders = await ordersAPI.getAll();

        // Transform API response to match our interface
        const transformedOrders = orders.map((order: any) => ({
          id: order.id,
          book_id: order.book_id,
          book_name: order.bookName || order.book_name,
          status: order.status,
          quantity: order.quantity,
          supplier_name: order.supplier_name,
          phone_number: order.phone_number,
          price_per_book: order.price_per_book,
          total_purchase: order.total_purchase,
          client_id: order.client_id,
        }));

        setAddedBooks(
          transformedOrders.filter((o: OrderListItem) => o.status === "added"),
        );
        setPendingBooks(
          transformedOrders.filter(
            (o: OrderListItem) => o.status === "pending",
          ),
        );
      } catch (error) {
        console.error("Error fetching orders:", error);
        // handle error
      }
    };

    fetchOrderList();

    // Fetch books and filter those at/below minimum stock
    const fetchData = async () => {
      try {
        // Load low stock books
        const response = await booksAPI.getAll();
        const booksData = response || [];

        // Get low stock books (quantity < 5)
        const lowStock = booksData.filter((book: Book) => {
          return (book.stock || 0) < 5;
        });

        setLowStockBooks(lowStock);
      } catch (error) {
        console.error("Error fetching low stock books:", error);
      }
    };

    fetchData();
  }, []);

  const handleModalSubmit = async () => {
    if (modalData) {
      const { id } = modalData;
      const book = addedBooks.find((book) => book.id === id);

      if (book) {
        // Prepare the data to update
        const updateData = {
          book_name: book.book_name,
          supplier_name: formValues.name,
          phone_number: formValues.phoneNumber,
          quantity: Number(formValues.quantity),
          status: "pending",
        };

        try {
          await ordersAPI.update(id, updateData);
          setAddedBooks((prevBooks) => prevBooks.filter((b) => b.id !== id));
          setPendingBooks((prevBooks) => [
            ...prevBooks,
            { ...book, ...updateData },
          ]);
          onClose();
          setFormValues({ name: "", phoneNumber: "", quantity: "" });
          setModalData(null);
        } catch (error) {
          console.error("Error updating order:", error);
          // handle error
        }
      }
    }
  };

  const handleBulkPlaceOrder = () => {
    if (!modalDataBulk || modalDataBulk.length === 0) return;

    // Open the modal
    onOpen();
  };

  const handleCompleteOrder = async (bookId: number) => {
    try {
      if (!completeFormValues.pricePerBook || !completeFormValues.quantity) {
        alert("Please fill in all required fields");
        return;
      }

      const pricePerBook = parseFloat(completeFormValues.pricePerBook);
      const quantity = parseInt(completeFormValues.quantity, 10);
      const totalPurchase = pricePerBook * quantity;

      // Update the order status to 'completed'
      await ordersAPI.complete(bookId, {
        price_per_book: pricePerBook,
        quantity: quantity,
        total_purchase: totalPurchase,
      });

      // Remove from pending books
      setPendingBooks((prevBooks) =>
        prevBooks.filter((book) => book.id !== bookId)
      );

      // Close the modal and reset form
      setCompleteModalOpen(false);
      setCompleteModalData(null);
      setCompleteFormValues({
        originalQuantity: "",
        quantity: "",
        pricePerBook: "",
        totalPurchase: "",
        price: "",
      });
    } catch (error) {
      console.error("Error completing order:", error);
      alert("An error occurred while completing the order.");
    }
  };

  const handleOpenBulkCompleteModal = () => {
    if (selectedBookspending.size === 0) {
      alert("Please select orders to complete");
      return;
    }

    const selectedOrders = pendingBooks.filter((book) =>
      selectedBookspending.has(book.id)
    );

    const bulkData = selectedOrders.map((order) => ({
      id: order.id,
      book_name: order.book_name,
      quantity: order.quantity.toString(),
      pricePerBook: order.price_per_book?.toString() || "",
      totalPurchase: order.total_purchase?.toString() || "",
      originalQuantity: order.quantity.toString(),
      price: "",
    }));

    setBulkCompleteData(bulkData);
    setBulkCompleteModalOpen(true);
  };

  const handleBulkCompleteOrders = async () => {
    if (!bulkCompleteData) return;

    try {
      // Validate data
      const hasInvalidData = bulkCompleteData.some(
        (item) =>
          !item.pricePerBook ||
          isNaN(parseFloat(item.pricePerBook)) ||
          !item.quantity ||
          isNaN(parseInt(item.quantity, 10))
      );

      if (hasInvalidData) {
        alert("Please fill in all required fields with valid numbers");
        return;
      }

      // Prepare requests
      const completeRequests = bulkCompleteData.map((item) => {
        const pricePerBook = parseFloat(item.pricePerBook);
        const quantity = parseInt(item.quantity, 10);
        const totalPurchase = pricePerBook * quantity;

        return ordersAPI.complete(item.id, {
          price_per_book: pricePerBook,
          quantity: quantity,
          total_purchase: totalPurchase,
        });
      });

      // Execute all requests
      await Promise.all(completeRequests);

      // Update state
      const completedIds = new Set(bulkCompleteData.map((item) => item.id));
      setPendingBooks((prevBooks) =>
        prevBooks.filter((book) => !completedIds.has(book.id))
      );
      setSelectedBookspending(new Set());

      // Close modal and reset data
      setBulkCompleteModalOpen(false);
      setBulkCompleteData(null);

      alert("Orders completed successfully!");
    } catch (error) {
      console.error("Error completing orders:", error);
      alert("An error occurred while completing the orders.");
    }
  };

  const updateBulkOrderPrice = (index: number, price: string) => {
    if (!bulkCompleteData) return;

    const updatedData = [...bulkCompleteData];
    updatedData[index].pricePerBook = price;
    updatedData[index].totalPurchase = (
      parseFloat(price || "0") * parseInt(updatedData[index].quantity || "0", 10)
    ).toString();

    setBulkCompleteData(updatedData);
  };

  const updateBulkOrderQuantity = (index: number, quantity: string) => {
    if (!bulkCompleteData) return;

    const updatedData = [...bulkCompleteData];
    updatedData[index].quantity = quantity;
    updatedData[index].totalPurchase = (
      parseFloat(updatedData[index].pricePerBook || "0") *
      parseInt(quantity || "0", 10)
    ).toString();

    setBulkCompleteData(updatedData);
  };

  const fetchAvailableBooks = async () => {
    try {
      const response = await booksAPI.getAll();
      setAvailableBooks(response || []);
    } catch (error) {
      console.error("Error fetching books:", error);
      alert("Failed to load books.");
    }
  };

  const handleOpenNewOrderModal = () => {
    fetchAvailableBooks();
    setNewOrderModalOpen(true);
  };

  const resetNewOrderForm = () => {
    setNewOrderFormValues({
      book_id: "",
      book_name: "",
      quantity: "1",
      supplier_name: "",
      phone_number: "",
      status: "pending",
    });
    setSelectedBookId("");
  };

  const handleBookSelection = (bookId: string) => {
    const selectedBook = availableBooks.find((book) => book.id === bookId);
    if (selectedBook) {
      setSelectedBookId(bookId);
      setNewOrderFormValues({
        ...newOrderFormValues,
        book_id: bookId,
        book_name: selectedBook.name,
      });
    }
  };

  const handleNewOrderSubmit = async () => {
    try {
      // Validate form
      if (
        !newOrderFormValues.book_id ||
        !newOrderFormValues.supplier_name ||
        !newOrderFormValues.quantity
      ) {
        alert("Please fill in all required fields");
        return;
      }

      // Create new order
      const newOrder = {
        book_id: newOrderFormValues.book_id,
        book_name: newOrderFormValues.book_name,
        quantity: parseInt(newOrderFormValues.quantity, 10),
        supplier_name: newOrderFormValues.supplier_name,
        phone_number: newOrderFormValues.phone_number,
        status: newOrderFormValues.status,
      };

      const response = await ordersAPI.create(newOrder);

      // Add to appropriate list based on status
      if (newOrder.status === "pending") {
        setPendingBooks((prev) => [
          ...prev,
          {
            ...newOrder,
            id: response.id,
            client_id: Number(response.client_id || 0),
          } as OrderListItem,
        ]);
      } else {
        setAddedBooks((prev) => [
          ...prev,
          {
            ...newOrder,
            id: response.id,
            client_id: Number(response.client_id || 0),
          } as OrderListItem,
        ]);
      }

      // Close modal and reset form
      setNewOrderModalOpen(false);
      resetNewOrderForm();
    } catch (error) {
      console.error("Error creating new order:", error);
      alert("Failed to create new order.");
    }
  };

  const handleBulkCancelOrder = async () => {
    if (selectedBookspending.size === 0) {
      alert("Please select orders to cancel");
      return;
    }

    const confirmCancel = window.confirm(
      `Are you sure you want to cancel ${selectedBookspending.size} orders?`
    );

    if (!confirmCancel) return;

    try {
      // Create an array of promises for each order to cancel
      const cancelPromises = Array.from(selectedBookspending).map((bookId) =>
        ordersAPI.cancel(bookId)
      );

      // Wait for all cancellations to complete
      await Promise.all(cancelPromises);

      // Update state by removing cancelled orders
      setPendingBooks((prevBooks) =>
        prevBooks.filter((book) => !selectedBookspending.has(book.id))
      );

      // Clear selection
      setSelectedBookspending(new Set());

      alert("Orders cancelled successfully!");
    } catch (error) {
      console.error("Error cancelling orders:", error);
      alert("An error occurred while cancelling the orders.");
    }
  };

  return (
    <DefaultLayout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">
          {t("Orders")}
        </h1>
        
        <div className="mb-4 flex justify-between items-center">
          <div>
            <Button 
              color="primary" 
              onClick={handleOpenNewOrderModal}
              className="mr-2"
            >
              {t("New Order")}
            </Button>
          </div>
          
          <div className="flex gap-2">
            {pendingBooks.length > 0 && (
              <>
                <Button 
                  color="success" 
                  onClick={handleOpenBulkCompleteModal}
                  isDisabled={selectedBookspending.size === 0}
                >
                  {t("Complete Selected")}
                </Button>
                <Button 
                  color="danger" 
                  onClick={handleBulkCancelOrder}
                  isDisabled={selectedBookspending.size === 0}
                >
                  {t("Cancel Selected")}
                </Button>
              </>
            )}
          </div>
        </div>
        
        <div className="flex flex-col">
          <div className="mb-4">
            <h2 className="text-xl font-semibold mb-2">{t("Pending Orders")}</h2>
            {pendingBooks.length > 0 ? (
              <Table aria-label="Pending orders table">
                <TableHeader>
                  <TableColumn>
                    <Checkbox
                      isSelected={selectedBookspending.size === pendingBooks.length && pendingBooks.length > 0}
                      onValueChange={(isSelected) => {
                        if (isSelected) {
                          setSelectedBookspending(new Set(pendingBooks.map(book => book.id)));
                        } else {
                          setSelectedBookspending(new Set());
                        }
                      }}
                    />
                  </TableColumn>
                  <TableColumn>{t("Book Name")}</TableColumn>
                  <TableColumn>{t("Quantity")}</TableColumn>
                  <TableColumn>{t("Supplier")}</TableColumn>
                  <TableColumn>{t("Phone")}</TableColumn>
                  <TableColumn>{t("Actions")}</TableColumn>
                </TableHeader>
                <TableBody>
                  {pendingBooks.map((book) => (
                    <TableRow key={book.id}>
                      <TableCell>
                        <Checkbox
                          isSelected={selectedBookspending.has(book.id)}
                          onValueChange={(isSelected) => {
                            const newSelected = new Set(selectedBookspending);
                            if (isSelected) {
                              newSelected.add(book.id);
                            } else {
                              newSelected.delete(book.id);
                            }
                            setSelectedBookspending(newSelected);
                          }}
                        />
                      </TableCell>
                      <TableCell>{book.book_name}</TableCell>
                      <TableCell>{book.quantity}</TableCell>
                      <TableCell>{book.supplier_name}</TableCell>
                      <TableCell>{book.phone_number}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          color="success"
                          onClick={() => {
                            setCompleteModalData({
                              id: book.id,
                              book_name: book.book_name,
                            });
                            setCompleteFormValues({
                              ...completeFormValues,
                              originalQuantity: book.quantity.toString(),
                              quantity: book.quantity.toString(),
                            });
                            setCompleteModalOpen(true);
                          }}
                        >
                          {t("Complete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p>{t("No pending orders")}</p>
            )}
          </div>
          
          {/* Add Books to Order section */}
          <div>
            <h2 className="text-xl font-semibold mb-2">{t("Books to Order")}</h2>
            {addedBooks.length > 0 ? (
              <>
                <Table aria-label="Books to order table">
                  <TableHeader>
                    <TableColumn>
                      <Checkbox
                        isSelected={selectedBooks.size === addedBooks.length && addedBooks.length > 0}
                        onValueChange={(isSelected) => {
                          if (isSelected) {
                            setSelectedBooks(new Set(addedBooks.map(book => book.id)));
                          } else {
                            setSelectedBooks(new Set());
                          }
                        }}
                      />
                    </TableColumn>
                    <TableColumn>{t("Book Name")}</TableColumn>
                    <TableColumn>{t("Quantity")}</TableColumn>
                    <TableColumn>{t("Actions")}</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {addedBooks.map((book) => (
                      <TableRow key={book.id}>
                        <TableCell>
                          <Checkbox
                            isSelected={selectedBooks.has(book.id)}
                            onValueChange={(isSelected) => {
                              const newSelected = new Set(selectedBooks);
                              if (isSelected) {
                                newSelected.add(book.id);
                              } else {
                                newSelected.delete(book.id);
                              }
                              setSelectedBooks(newSelected);
                            }}
                          />
                        </TableCell>
                        <TableCell>{book.book_name}</TableCell>
                        <TableCell>{book.quantity}</TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            color="primary"
                            onClick={() => {
                              setModalData({
                                id: book.id,
                                book_name: book.book_name,
                              });
                              setFormValues({
                                ...formValues,
                                quantity: book.quantity.toString(),
                              });
                              onOpen();
                            }}
                          >
                            {t("Place Order")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {selectedBooks.size > 0 && (
                  <div className="mt-4">
                    <Button 
                      color="primary"
                      onClick={() => {
                        const selectedItems = addedBooks.filter(book => selectedBooks.has(book.id));
                        setModalDataBulk(
                          selectedItems.map(book => ({
                            id: book.id,
                            book_name: book.book_name,
                            quantity: book.quantity.toString(),
                          }))
                        );
                        handleBulkPlaceOrder();
                      }}
                    >
                      {t("Place Order for Selected")}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <p>{t("No books to order")}</p>
            )}
          </div>
        </div>
        
        {/* Place Order Modal */}
        <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
          <ModalContent>
            {() => (
              <>
                <ModalHeader>
                  {modalDataBulk
                    ? t("Place Bulk Order")
                    : `${t("Place Order for")} ${modalData?.book_name}`}
                </ModalHeader>
                <ModalBody>
                  {modalDataBulk ? (
                    <div>
                      <p>{t("You are placing orders for")} {modalDataBulk.length} {t("books")}.</p>
                      <Input
                        label={t("Supplier Name")}
                        placeholder={t("Enter supplier name")}
                        value={formValues.name}
                        onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                        className="mb-2"
                      />
                      <Input
                        label={t("Supplier Phone Number")}
                        placeholder={t("Enter phone number")}
                        value={formValues.phoneNumber}
                        onChange={(e) => setFormValues({ ...formValues, phoneNumber: e.target.value })}
                      />
                    </div>
                  ) : (
                    <>
                      <Input
                        label={t("Supplier Name")}
                        placeholder={t("Enter supplier name")}
                        value={formValues.name}
                        onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                        className="mb-2"
                      />
                      <Input
                        label={t("Supplier Phone Number")}
                        placeholder={t("Enter phone number")}
                        value={formValues.phoneNumber}
                        onChange={(e) => setFormValues({ ...formValues, phoneNumber: e.target.value })}
                        className="mb-2"
                      />
                      <Input
                        label={t("Quantity")}
                        placeholder={t("Enter quantity")}
                        type="number"
                        min="1"
                        value={formValues.quantity}
                        onChange={(e) => setFormValues({ ...formValues, quantity: e.target.value })}
                      />
                    </>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button color="danger" variant="flat" onClick={onClose}>
                    {t("Cancel")}
                  </Button>
                  <Button 
                    color="primary" 
                    onClick={modalDataBulk ? async () => {
                      // Handle bulk order submit
                      if (!modalDataBulk || !formValues.name) return;
                      
                      try {
                        const updatePromises = modalDataBulk.map(item => 
                          ordersAPI.update(item.id, {
                            book_name: item.book_name,
                            supplier_name: formValues.name,
                            phone_number: formValues.phoneNumber,
                            quantity: Number(item.quantity),
                            status: "pending",
                          })
                        );
                        
                        await Promise.all(updatePromises);
                        
                        // Update local state
                        const updatedIds = new Set(modalDataBulk.map(item => item.id));
                        
                        setAddedBooks(prevBooks => 
                          prevBooks.filter(book => !updatedIds.has(book.id))
                        );
                        
                        const updatedBooks = modalDataBulk.map(item => ({
                          id: item.id,
                          book_id: -1, // Placeholder, will be overwritten if found
                          book_name: item.book_name,
                          status: "pending",
                          quantity: Number(item.quantity),
                          supplier_name: formValues.name,
                          phone_number: formValues.phoneNumber,
                          client_id: -1, // Placeholder
                        }));
                        
                        // Find and use original book details where possible
                        addedBooks.forEach(original => {
                          if (updatedIds.has(original.id)) {
                            const match = updatedBooks.find(book => book.id === original.id);
                            if (match) {
                              // Use type assertion to match the OrderListItem type
                              (match as any).book_id = original.book_id;
                              (match as any).client_id = original.client_id;
                            }
                          }
                        });
                        
                        setPendingBooks(prev => [...prev, ...updatedBooks as OrderListItem[]]);
                        
                        // Clean up
                        setSelectedBooks(new Set());
                        setFormValues({ name: "", phoneNumber: "", quantity: "" });
                        setModalDataBulk(null);
                        onClose();
                        
                      } catch (error) {
                        console.error("Error placing bulk orders:", error);
                        alert("Failed to place orders");
                      }
                    } : handleModalSubmit}
                  >
                    {t("Submit")}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
        
        {/* Complete Order Modal */}
        <Modal isOpen={isCompleteModalOpen} onOpenChange={setCompleteModalOpen}>
          <ModalContent>
            {() => (
              <>
                <ModalHeader>
                  {t("Complete Order for")} {completeModalData?.book_name}
                </ModalHeader>
                <ModalBody>
                  <Input
                    label={t("Original Quantity")}
                    value={completeFormValues.originalQuantity}
                    readOnly
                    className="mb-2"
                  />
                  <Input
                    label={t("Actual Quantity")}
                    placeholder={t("Enter actual quantity received")}
                    type="number"
                    min="1"
                    value={completeFormValues.quantity}
                    onChange={(e) => setCompleteFormValues({ 
                      ...completeFormValues, 
                      quantity: e.target.value,
                      totalPurchase: (parseFloat(completeFormValues.pricePerBook || "0") * parseInt(e.target.value || "0", 10)).toString()
                    })}
                    className="mb-2"
                  />
                  <Input
                    label={t("Price Per Book")}
                    placeholder={t("Enter price per book")}
                    type="number"
                    min="0"
                    step="0.01"
                    value={completeFormValues.pricePerBook}
                    onChange={(e) => setCompleteFormValues({ 
                      ...completeFormValues, 
                      pricePerBook: e.target.value,
                      totalPurchase: (parseFloat(e.target.value || "0") * parseInt(completeFormValues.quantity || "0", 10)).toString()
                    })}
                    className="mb-2"
                  />
                  <Input
                    label={t("Total Purchase Amount")}
                    type="number"
                    value={String(completeFormValues.totalPurchase || 
                      parseFloat(completeFormValues.pricePerBook || "0") * 
                      parseInt(completeFormValues.quantity || "0", 10)
                    )}
                    readOnly
                  />
                </ModalBody>
                <ModalFooter>
                  <Button color="danger" variant="flat" onClick={() => setCompleteModalOpen(false)}>
                    {t("Cancel")}
                  </Button>
                  <Button 
                    color="success" 
                    onClick={() => completeModalData && handleCompleteOrder(completeModalData.id)}
                  >
                    {t("Complete Order")}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
        
        {/* Bulk Complete Orders Modal */}
        <Modal 
          isOpen={bulkCompleteModalOpen} 
          onOpenChange={setBulkCompleteModalOpen}
          size="xl"
        >
          <ModalContent>
            {() => (
              <>
                <ModalHeader>
                  {t("Complete Multiple Orders")}
                </ModalHeader>
                <ModalBody>
                  <div className="overflow-y-auto max-h-[60vh]">
                    <Table aria-label="Orders to complete">
                      <TableHeader>
                        <TableColumn>{t("Book")}</TableColumn>
                        <TableColumn>{t("Original Qty")}</TableColumn>
                        <TableColumn>{t("Actual Qty")}</TableColumn>
                        <TableColumn>{t("Price/Book")}</TableColumn>
                        <TableColumn>{t("Total")}</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {(bulkCompleteData || []).map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.book_name}</TableCell>
                            <TableCell>{item.originalQuantity}</TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateBulkOrderQuantity(index, e.target.value)}
                                aria-label={`Quantity for ${item.book_name}`}
                                size="sm"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.pricePerBook}
                                onChange={(e) => updateBulkOrderPrice(index, e.target.value)}
                                aria-label={`Price for ${item.book_name}`}
                                size="sm"
                              />
                            </TableCell>
                            <TableCell>
                              {(parseFloat(item.pricePerBook || "0") * parseInt(item.quantity || "0", 10)).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button color="danger" variant="flat" onClick={() => setBulkCompleteModalOpen(false)}>
                    {t("Cancel")}
                  </Button>
                  <Button 
                    color="success" 
                    onClick={handleBulkCompleteOrders}
                  >
                    {t("Complete All Orders")}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
        
        {/* New Order Modal */}
        <Modal 
          isOpen={isNewOrderModalOpen} 
          onOpenChange={setNewOrderModalOpen}
        >
          <ModalContent>
            {() => (
              <>
                <ModalHeader>
                  {t("Create New Order")}
                </ModalHeader>
                <ModalBody>
                  <Dropdown>
                    <DropdownTrigger>
                      <Button 
                        variant="bordered" 
                        className="w-full justify-between"
                        endContent={<ChevronDownIcon />}
                      >
                        {selectedBookId 
                          ? availableBooks.find(b => b.id === selectedBookId)?.name
                          : t("Select a book")}
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu 
                      aria-label="Book selection"
                      className="max-h-64 overflow-y-auto"
                      items={availableBooks}
                    >
                      {(book) => (
                        <DropdownItem 
                          key={book.id} 
                          onClick={() => handleBookSelection(book.id)}
                        >
                          {book.name}
                        </DropdownItem>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                  
                  <Input
                    label={t("Quantity")}
                    placeholder={t("Enter quantity")}
                    type="number"
                    min="1"
                    value={newOrderFormValues.quantity}
                    onChange={(e) => setNewOrderFormValues({ 
                      ...newOrderFormValues,
                      quantity: e.target.value
                    })}
                    className="mt-2"
                  />
                  
                  <Input
                    label={t("Supplier Name")}
                    placeholder={t("Enter supplier name")}
                    value={newOrderFormValues.supplier_name}
                    onChange={(e) => setNewOrderFormValues({ 
                      ...newOrderFormValues,
                      supplier_name: e.target.value
                    })}
                    className="mt-2"
                  />
                  
                  <Input
                    label={t("Supplier Phone")}
                    placeholder={t("Enter supplier phone number")}
                    value={newOrderFormValues.phone_number}
                    onChange={(e) => setNewOrderFormValues({ 
                      ...newOrderFormValues,
                      phone_number: e.target.value
                    })}
                    className="mt-2"
                  />
                  
                  <Dropdown className="mt-2">
                    <DropdownTrigger>
                      <Button 
                        variant="bordered" 
                        className="w-full justify-between"
                        endContent={<ChevronDownIcon />}
                      >
                        {newOrderFormValues.status === "pending" 
                          ? t("Pending Order") 
                          : t("Add to Order List")}
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Order status selection">
                      <DropdownItem key="pending" onClick={() => setNewOrderFormValues({
                        ...newOrderFormValues,
                        status: "pending"
                      })}>
                        {t("Pending Order")}
                      </DropdownItem>
                      <DropdownItem key="added" onClick={() => setNewOrderFormValues({
                        ...newOrderFormValues,
                        status: "added"
                      })}>
                        {t("Add to Order List")}
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </ModalBody>
                <ModalFooter>
                  <Button 
                    color="danger" 
                    variant="flat" 
                    onClick={() => {
                      setNewOrderModalOpen(false);
                      resetNewOrderForm();
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button 
                    color="primary" 
                    onClick={handleNewOrderSubmit}
                  >
                    {t("Create Order")}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      </div>
    </DefaultLayout>
  );
};

export default OrderPage;
